import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import Cookies from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags';
import type { Perimeter } from '@atlassian/atl-context';

import { CategoryStorageType } from '../../../types';
import { CacheLoader } from '../load-storage-controls-data';
import { Logger } from '../logger';

import {
	__internalApplyInitialState,
	__internalGetAllowUncategorizedCookiesInOverridesGlobalState,
	__internalGetDisableIndexedDbConsentChecksGlobalState,
	__internalGetHasInitialized,
	__internalGetIndexedDbControlsEnabledGlobalState,
	__internalGetProductGlobalState,
	__internalGetWebStorageControlsEnabledGlobalState,
} from './core';

export interface InitializeBscGlobalStateParams {
	// Common params
	analyticsEnabled?: boolean;
	product?: string;
	perimeter?: Perimeter;
	localConsentMode?: boolean;

	// InitializeControls only
	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	allowUncategorizedCookiesInOverrides?: boolean;
	disableIndexedDbConsentChecks?: boolean;
	userId?: string;
	cloudId?: string;
}

export const initializeGlobalState = ({
	webStorageEnabled,
	indexedDbEnabled,
	allowUncategorizedCookiesInOverrides,
	disableIndexedDbConsentChecks,
	localConsentMode = false,
	product = 'all',
	perimeter = 'commercial',
	analyticsEnabled = true,
	userId,
	cloudId,
}: InitializeBscGlobalStateParams = {}): void => {
	Logger.debug('Initialize global state invoked');

	const productGlobalState = __internalGetProductGlobalState();

	// If the app provides a value for product only in one place,
	// we want that value to be sticky and to override the default if necessary
	if (
		(!productGlobalState || productGlobalState == null || productGlobalState === 'all') &&
		product
	) {
		__internalApplyInitialState({ productGlobalState: product });
	}

	// Treating webStorageEnabled, indexedDbEnabled, and allowUncategorizedCookiesInOverrides differently
	// because they are only passed through initializeControls and
	// we don't want it to be defaulted to false via a call from the ConsentBanner
	const webStorageControlsEnabledGlobalState = __internalGetWebStorageControlsEnabledGlobalState();
	if (webStorageEnabled !== undefined && webStorageControlsEnabledGlobalState === undefined) {
		__internalApplyInitialState({
			webStorageControlsEnabledGlobalState:
				webStorageEnabled &&
				!fg('platform_moonjelly_bsc_master_killswitch') &&
				!fg('platform_moonjelly_bsc_web_storage_killswitch'),
		});
	}

	const indexedDbControlsEnabledGlobalState = __internalGetIndexedDbControlsEnabledGlobalState();
	if (indexedDbEnabled !== undefined && indexedDbControlsEnabledGlobalState === undefined) {
		__internalApplyInitialState({
			indexedDbControlsEnabledGlobalState:
				indexedDbEnabled &&
				!fg('platform_moonjelly_bsc_master_killswitch') &&
				!fg('platform_moonjelly_bsc_web_storage_killswitch'),
		});
	}

	const allowUncategorizedCookiesInOverridesGlobalState =
		__internalGetAllowUncategorizedCookiesInOverridesGlobalState();
	if (
		allowUncategorizedCookiesInOverrides !== undefined &&
		allowUncategorizedCookiesInOverridesGlobalState === undefined
	) {
		__internalApplyInitialState({
			allowUncategorizedCookiesInOverridesGlobalState: allowUncategorizedCookiesInOverrides,
		});
	}

	const disableIndexedDbConsentChecksGlobalState =
		__internalGetDisableIndexedDbConsentChecksGlobalState();
	if (
		disableIndexedDbConsentChecks !== undefined &&
		disableIndexedDbConsentChecksGlobalState === undefined
	) {
		__internalApplyInitialState({
			disableIndexedDbConsentChecksGlobalState: disableIndexedDbConsentChecks,
		});
	}

	// All other state can only be initialized once and is defaulted if not passed on the first initialization
	if (__internalGetHasInitialized()) {
		return;
	}

	let localConsentModeGlobalState: boolean;
	if (!fg('fedramp-cookies-control-v2')) {
		localConsentModeGlobalState = localConsentMode;
	} else if (fg('jfp-magma-aem-cookie-check')) {
		// Force local consent mode when ConsentHub is not enabled because ConsentHub is
		// not hydrated in the perimeter as of this comment
		localConsentModeGlobalState =
			localConsentMode || !isFeatureEnabled('consent-platform', 'consenthub');
	} else {
		// Force local consent mode when in the FedRAMP Moderate perimeter because ConsentHub is
		// not hydrated in the perimeter as of this comment
		localConsentModeGlobalState = localConsentMode || perimeter === 'fedramp-moderate';
	}

	const cookiesCacheLoader = new CacheLoader(CategoryStorageType.Cookies, perimeter);
	const localStorageCacheLoader = new CacheLoader(CategoryStorageType.LocalStorage, perimeter);
	const sessionStorageCacheLoader = new CacheLoader(CategoryStorageType.SessionStorage, perimeter);
	const indexedDBCacheLoader = new CacheLoader(CategoryStorageType.IndexedDB, perimeter);

	let packageDebugModeEnabledGlobalState: boolean | undefined;
	if (fg('platform_moonjelly_bsc_debug_mode')) {
		packageDebugModeEnabledGlobalState = Boolean(Cookies.get('atl-bsc-debug-mode'));
	}

	const updates: Parameters<typeof __internalApplyInitialState>[0] = {
		localConsentModeGlobalState,
		analyticsEnabledGlobalState: analyticsEnabled,
		perimeterGlobalState: perimeter,
		cookiesCacheLoader,
		localStorageCacheLoader,
		sessionStorageCacheLoader,
		indexedDBCacheLoader,
		packageDebugModeEnabledGlobalState,
		hasInitialized: true,
	};

	if (cloudId) {
		updates.cloudIdGlobalState = cloudId;
	}

	if (userId) {
		updates.userIdGlobalState = userId;
	}

	__internalApplyInitialState(updates);
};
